import { AlertTriangle, X, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";

/**
 * Displays a warning banner when the user's GitHub PAT is expiring soon (< 14 days)
 * or already expired.
 *
 * Props:
 * daysUntilExpiry – number (0 = expired today, negative = already expired)
 * onDismiss – () => void (called when user closes the banner)
 */
const GitHubPATBanner = ({ daysUntilExpiry, onDismiss }) => {
  const navigate = useNavigate();

  if (daysUntilExpiry === null || daysUntilExpiry === undefined || daysUntilExpiry > 14) {
    return null;
  }

  const expired = daysUntilExpiry <= 0;
  const urgent = daysUntilExpiry <= 3;

  const bgClass = expired || urgent
    ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300"
    : "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300";

  const iconClass = expired || urgent ? "text-red-500 dark:text-red-400" : "text-amber-500 dark:text-amber-400";

  const message = expired
    ? "Your GitHub token has expired. GitHub features are unavailable."
    : daysUntilExpiry === 1
    ? "Your GitHub token expires tomorrow."
    : `Your GitHub token expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? "" : "s"}.`;

  return (
    <div className={`w-full border-b px-4 py-2.5 flex items-center gap-3 text-sm ${bgClass}`} role="alert">
      <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${iconClass}`} />
      <span className="flex-1">{message}</span>
      <Button
        variant="link"
        size="sm"
        className={`h-auto p-0 font-medium underline-offset-2 ${expired || urgent ? "text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100" : "text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100"}`}
        onClick={() => navigate("/profile", { state: { openGitHubSettings: true } })}
      >
        Update token
        <ExternalLink className="w-3 h-3 ml-1" />
      </Button>
      {onDismiss && (
        <button
          className={`p-1 rounded hover:bg-black/5 ${iconClass}`}
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default GitHubPATBanner;
